/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import React, { useMemo } from 'react';
import { Button, Flex, Select, Spin } from 'antd';
import {
  BookOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import classNames from 'classnames';
import styles from './RequestMethodLearning.module.less';
import AutoSizeInput from '../../AutoSizeInput';
import MarkdownRenderer from '../../MarkdownRenderer';
import {
  LearnedAnswer,
  WorkerForkExecution,
  useGetLearnedAnswerQuery
} from '../../../generated/graphql';
import { WorkerState } from '../../../pages/PlaygroundV3/components/WorkerStatusInfo/interface';
import FormattingUtil from '../../../utils/FormattingUtil';
import {
  MethodLearningState,
  MiniPlaygroundSourceType
} from '../../MiniPlayground/interface';
import WorkerStateUtil from '../../../utils/WorkerStateUtil';
import { OpenMiniPlaygroundMethod } from '../interface';
import { getQuestionInputValueFromAnswer } from '../../../utils/request';

interface IRequestMethodLearningProps {
  forkedWorker: WorkerForkExecution;
  isAnswered: boolean;
  onMethodLearningReset: () => void;
  handleOpenMiniPlayground: OpenMiniPlaygroundMethod;
}

function RequestMethodLearning(props: IRequestMethodLearningProps) {
  const {
    forkedWorker,
    isAnswered,
    onMethodLearningReset,
    handleOpenMiniPlayground
  } = props;

  const state = forkedWorker.internalState as WorkerState;
  const stateLifecycle = WorkerStateUtil.getWorkerStateLifecycle(state);

  const loading = stateLifecycle[MethodLearningState.LOADING];
  const finished = stateLifecycle[MethodLearningState.FINISHED] && isAnswered;
  const failed = !finished;

  const { data, loading: learnedAnswerLoading } = useGetLearnedAnswerQuery({
    variables: {
      id: forkedWorker.metadata?.learningId as string
    },
    skip: !forkedWorker.metadata?.learningId
  });

  const learnedAnswer = data?.getLearnedAnswer as LearnedAnswer | undefined;

  const methodBody = useMemo(() => {
    if (learnedAnswer) {
      const answerText = getQuestionInputValueFromAnswer(learnedAnswer);
      const decoded = FormattingUtil.parseBrainValue(answerText);
      return decoded;
    }
    return '';
  }, [learnedAnswer]);

  const renderTitle = () => {
    if (loading) {
      return 'Applying a learning';
    }
    if (failed) {
      return 'Failed to apply learning';
    }
    if (finished) {
      return 'Learning Applied';
    }
    return null;
  };

  const renderTitleIcon = () => {
    if (loading) {
      return <LoadingOutlined />;
    }
    if (failed) {
      return <ExclamationCircleOutlined />;
    }
    if (finished) {
      return <CheckCircleOutlined />;
    }
    return null;
  };

  const renderSelectSuffixIcon = () => {
    if (failed) {
      return <CloseCircleOutlined />;
    }
    return <CheckCircleOutlined />;
  };

  const parsedAnswer = FormattingUtil.parseBrainValue(
    forkedWorker.answer || ''
  );

  return (
    <div className={styles.requestMethodLearning}>
      <div className={styles.header}>
        <Flex justify="space-between" align="center">
          <div className={styles.title}>{renderTitle()}</div>
          <div className={styles.titleIcon}>{renderTitleIcon()}</div>
        </Flex>

        <Select
          disabled
          loading={false}
          placeholder="Teach a method"
          suffixIcon={renderSelectSuffixIcon()}
          className={styles.select}
        />
        <AutoSizeInput
          value={failed ? '' : parsedAnswer}
          disabled
          size={{
            maxRows: 10
          }}
          bordered
          className={styles.input}
        />
      </div>

      {failed && (
        <div className={styles.resetButton}>
          <Button type="primary" onClick={onMethodLearningReset}>
            Answer manually
          </Button>
        </div>
      )}

      <div className={styles.content}>
        <Flex justify="space-between" align="center">
          <div className={styles.methodUsedTitle}>Method used</div>
          <BookOutlined className={styles.bookIcon} />
        </Flex>

        <Spin spinning={learnedAnswerLoading || loading}>
          <div
            className={classNames(styles.methodBody, {
              [styles.methodBodyFailed]: failed,
              [styles.methodBodyLoading]: loading,
              [styles.methodBodySuccess]: finished
            })}
            onClick={() => {
              if (loading) {
                return;
              }

              handleOpenMiniPlayground({
                source: MiniPlaygroundSourceType.LEARNING_METHOD,
                miniPlaygroundToken: forkedWorker.documentToken as string,
                metadata: {
                  methodLearningId:
                    forkedWorker.metadata?.learningId || undefined,
                  methodLearningStateLifecycle: {
                    [MethodLearningState.LOADING]: loading,
                    [MethodLearningState.FINISHED]: finished,
                    [MethodLearningState.FAILED]: failed
                  },
                  methodLearningText: methodBody
                }
              });
            }}
          >
            <MarkdownRenderer>{methodBody}</MarkdownRenderer>
          </div>
        </Spin>
      </div>
    </div>
  );
}

export default RequestMethodLearning;
